import {
  CheckboxControl,
  Form,
  setAuthCookies,
  TextFieldControl,
  useToast,
  VALIDATION,
} from '@foxtaibox/foxtailbox-frontend-core';
import {
  AssessmentWebTrialListOrderFields,
  OrderType,
  useRegisterCandidateByPublicExaminationMutation,
  useTrialsQuery,
} from '@generated/graphql';
import { Button, Link, Stack } from '@mui/material';
import PhoneNumberFieldControl from 'components/form/FieldControls/PhoneNumberFieldControl';
import { EXAMINATION_IS_NOT_AVAILABLE_ERROR_MESSAGE } from 'constants/assessment';
import { ACCESS_TOKEN_NAME, REFRESH_TOKEN_NAME } from 'constants/cookies';
import ROUTES from 'constants/routes';
import { Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { setIsPublicExamination } from 'store/assessment/model';
import { getApolloErrorMessage } from 'utils/errorsParse';
import { authFieldSx } from '../styles';

interface IRegisterFormData {
  fullName: string;
  email?: string;
  phoneNumber?: string;
  confirmAgreement: boolean;
}

interface RegisterFormProps {
  examinationId: string;
  setShowAssesmentAccessCard: Dispatch<SetStateAction<boolean>>;
}

const ExaminationRegisterForm = ({
  examinationId,
  setShowAssesmentAccessCard,
}: RegisterFormProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showErrorToast } = useToast();
  const form = useForm<IRegisterFormData>({
    values: {
      fullName: '',
      confirmAgreement: false,
    },
  });
  const { handleSubmit } = form;
  const [registerCandidateByPublicExamination, { loading }] =
    useRegisterCandidateByPublicExaminationMutation();

  const { refetch } = useTrialsQuery({
    variables: {
      filter: {},
      limit: 1,
      page: 0,
      order: {
        field: AssessmentWebTrialListOrderFields.CreatedAt,
        order: OrderType.Desc,
      },
    },
    fetchPolicy: 'standby',
  });

  const onSubmit = ({ confirmAgreement, ...formData }: IRegisterFormData) => {
    registerCandidateByPublicExamination({
      variables: {
        input: {
          examinationId,
          ...formData,
        },
      },
    })
      .then((r) => {
        const accessToken = r.data?.registerCandidateByPublicExamination.accessToken;
        const refreshToken = r.data?.registerCandidateByPublicExamination.refreshToken;

        if (accessToken && refreshToken) {
          const newCookies = [
            { name: ACCESS_TOKEN_NAME, value: accessToken },
            { name: REFRESH_TOKEN_NAME, value: refreshToken },
          ];

          setAuthCookies(newCookies);
          setIsPublicExamination(true);

          refetch().then((res) => {
            const assesmentId = res.data.trials.data?.[0].id;

            if (!assesmentId) {
              navigate(ROUTES.ASSESSMENT);
              showErrorToast(t('errors.common'));
            } else {
              navigate(`${ROUTES.ASSESSMENT}/${assesmentId}`);
            }
          });
        } else {
          showErrorToast(t('errors.common'));
        }
      })
      .catch((err) => {
        const errorMessage = getApolloErrorMessage(err);

        if (errorMessage === EXAMINATION_IS_NOT_AVAILABLE_ERROR_MESSAGE) {
          setShowAssesmentAccessCard(true);
        } else {
          showErrorToast(t('errors.common'));
        }
      });
  };

  return (
    <Form form={form}>
      <Stack spacing={3}>
        <TextFieldControl
          name='fullName'
          label={t('user.fullNameLabel')}
          placeholder={t('user.fullNameLabel')}
          type='text'
          rules={{
            required: true,
            ...VALIDATION({
              message: t('validation.userName'),
              maxLengthMessage: (count) => t('validation.maxLengthExceeded', { count }),
            }).userName,
          }}
          disabled={loading}
          sx={authFieldSx}
        />
        <PhoneNumberFieldControl disabled={loading} sx={authFieldSx} />
        <TextFieldControl
          name='email'
          label='Email'
          placeholder='Email'
          type='email'
          rules={{
            ...VALIDATION({
              message: t('validation.invalidEmailCharacters'),
              maxLengthMessage: (count) => t('validation.maxLengthExceeded', { count }),
            }).emailFull,
          }}
          disabled={loading}
          sx={authFieldSx}
        />
        <CheckboxControl
          rules={{ required: t('confirmUserData.confirmPermission') }}
          name='confirmAgreement'
          label={
            <>
              {t('confirmUserData.agreement.1')}{' '}
              <Link
                href='https://docs.google.com/document/d/1nltBSYJig6bLmnSN6KmiVSDrRmfJl4bCKwfIBKlF7c0/edit?tab=t.0'
                target='_blank'>
                {t('confirmUserData.agreement.2')}
              </Link>{' '}
              {t('confirmUserData.agreement.3')}{' '}
              <Link
                href='https://docs.google.com/document/d/1Q0ygJ1UmfkbXHLW5vX2tI6Rkx8Jv0YY8blyqguw5d9E/edit?tab=t.0'
                target='_blank'>
                {t('confirmUserData.agreement.4')}
                {t('confirmUserData.agreement.5')}
              </Link>
            </>
          }
        />
        <Button variant='contained' size='large' loading={loading} onClick={handleSubmit(onSubmit)}>
          Далее
        </Button>
      </Stack>
    </Form>
  );
};

export default ExaminationRegisterForm;
